/// <reference types="vite/client" />
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { workbenchBureauApi } from "../../../api/client";
import {
  type BureauApplicantInput,
  type BureauDataView,
  type BureauReportView,
  type BureauViewResponse,
  toBureauApplicant,
  toBureauReport,
  toSalesPersonName,
} from "./types";
import { viewBureauKeys } from "./viewBureauKeys";

export interface UseViewBureauOptions {
  approvalId: string;
  /** Name/SSN/applicantId for the driver, sourced from the dashboard response. */
  driver: BureauApplicantInput;
  /** Undefined when the deal has no co-maker. */
  secondSigner?: BureauApplicantInput;
  /** Only fetch while the modal is open — mirrors `useStipulationHistory`'s expanded gate. */
  enabled: boolean;
}

export interface UseViewBureauResult {
  data: BureauDataView | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Fetches the View Bureau modal payload for an approval. Backed by the
 * generated `BureauApi.getBureauView(approvalId, applicantId)`, a per-applicant
 * read (score + frozen flag + single bureau report, enriched with sales
 * person) — issued once for the driver and, when the deal has a co-maker,
 * again for the 2nd signer. The two responses are merged with the
 * caller-supplied name/SSN (the bureau endpoint itself doesn't return those)
 * into a single {@link BureauDataView} for the modal to render.
 * @param {UseViewBureauOptions} options - Approval id, driver/secondSigner identity, and whether the modal is open.
 * @returns {UseViewBureauResult} The combined query result.
 */
export function useViewBureau({
  approvalId,
  driver,
  secondSigner,
  enabled,
}: UseViewBureauOptions): UseViewBureauResult {
  const { t } = useTranslation();

  // Only register a 2nd-signer query when the deal actually has one — an
  // always-present-but-disabled second entry would collide on the same
  // ("", "") cache key as the driver's when both applicantIds are absent,
  // which TanStack Query flags as a "Duplicate Queries" warning.
  const queries = useQueries({
    queries: [
      {
        queryKey: viewBureauKeys.detail(approvalId, driver.applicantId),
        queryFn: async ({ signal }: { signal: AbortSignal }): Promise<BureauViewResponse> => {
          const response = await workbenchBureauApi.getBureauView(approvalId, driver.applicantId, { signal });
          return response.data as BureauViewResponse;
        },
        enabled: enabled && !!approvalId && !!driver.applicantId,
        staleTime: 0,
      },
      ...(secondSigner
        ? [
            {
              queryKey: viewBureauKeys.detail(approvalId, secondSigner.applicantId),
              queryFn: async ({ signal }: { signal: AbortSignal }): Promise<BureauViewResponse> => {
                const response = await workbenchBureauApi.getBureauView(approvalId, secondSigner.applicantId, {
                  signal,
                });
                return response.data as BureauViewResponse;
              },
              enabled: enabled && !!approvalId && !!secondSigner.applicantId,
              staleTime: 0,
            },
          ]
        : []),
    ],
  });
  const [driverQuery, secondSignerQuery] = queries;

  const isLoading = driverQuery.isLoading || (!!secondSignerQuery && secondSignerQuery.isLoading);
  const isError = driverQuery.isError || (!!secondSignerQuery && secondSignerQuery.isError);
  const error = driverQuery.error ?? secondSignerQuery?.error;

  const isReady = !!driverQuery.data && (!secondSignerQuery || !!secondSignerQuery.data);
  const data: BureauDataView | undefined = isReady
    ? {
        salesPerson: toSalesPersonName(driverQuery.data?.salesPerson),
        approvalNumber: approvalId,
        driver: toBureauApplicant(driver, driverQuery.data),
        secondSigner:
          secondSigner && secondSignerQuery?.data
            ? toBureauApplicant(secondSigner, secondSignerQuery.data as BureauViewResponse)
            : undefined,
        reports: [
          toBureauReport(t("viewBureau.driver"), driverQuery.data),
          secondSigner
            ? toBureauReport(t("viewBureau.secondSigner"), secondSignerQuery?.data as BureauViewResponse)
            : undefined,
        ].filter((report): report is BureauReportView => !!report),
      }
    : undefined;

  return { data, isLoading, isError, error };
}
